import { useState } from 'react';
import { useChild } from '../../contexts/ChildContext';
import Logo from './Logo';

const GENDER_OPTIONS = [
  { value: 'female', label: 'Girl',  emoji: '👧' },
  { value: 'male',   label: 'Boy',   emoji: '👦' },
  { value: 'other',  label: 'Other', emoji: '🧒' },
];

/**
 * FirstChildSetup — full-screen welcome flow shown when a new user has no
 * children yet. Collects name, date of birth, and gender, then creates the
 * child profile via ChildContext.addChild().
 *
 * Not dismissable — the user must add at least one child to use the app.
 */
export default function FirstChildSetup() {
  const { addChild } = useChild();

  const [name, setName]     = useState('');
  const [dob, setDob]       = useState('');
  const [gender, setGender] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter a name.'); return; }
    if (!dob)         { setError('Please enter a date of birth.'); return; }
    setSaving(true);
    setError('');
    try {
      await addChild({ name: name.trim(), dateOfBirth: dob, gender });
    } catch (err) {
      console.error('Error adding child:', err);
      setError('Something went wrong — please try again.');
      setSaving(false);
    }
    // On success, ChildContext sets selectedChild and AppLayout re-renders;
    // this component unmounts automatically.
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'var(--bg-white)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9500,
      padding: '1.5rem',
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Logo variant="full" size={32} />
        </div>

        {/* Welcome heading */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>👶</div>
          <h2 style={{ fontWeight: 700, fontSize: '1.4rem', color: 'var(--text-dark)', marginBottom: '0.4rem' }}>
            Welcome! Let's get set up.
          </h2>
          <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', margin: 0 }}>
            Tell us about your child to get started. You can add more children later.
          </p>
        </div>

        {/* Form card */}
        <div className="card" style={{ borderRadius: '1rem' }}>
          <div className="card-body" style={{ padding: '1.5rem' }}>
            <form onSubmit={handleSubmit}>

              {/* Name */}
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 600 }}>
                  Child's name
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Oliver"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setError(''); }}
                  autoFocus
                  maxLength={40}
                  required
                />
              </div>

              {/* Date of birth */}
              <div className="mb-3">
                <label className="form-label" style={{ fontWeight: 600 }}>
                  Date of birth
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={dob}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => { setDob(e.target.value); setError(''); }}
                  required
                />
              </div>

              {/* Gender */}
              <div className="mb-4">
                <label className="form-label" style={{ fontWeight: 600 }}>
                  Gender <span style={{ color: 'var(--text-light)', fontWeight: 400 }}>(optional)</span>
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {GENDER_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setGender(gender === opt.value ? '' : opt.value)}
                      style={{
                        flex: 1,
                        padding: '0.6rem 0.25rem',
                        borderRadius: '0.75rem',
                        border: `2px solid ${gender === opt.value ? 'var(--primary-dark)' : 'var(--border-light)'}`,
                        background: gender === opt.value ? 'var(--primary-soft)' : 'var(--bg-white)',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontWeight: gender === opt.value ? 700 : 400,
                        fontSize: '0.82rem',
                        color: 'var(--text-dark)',
                        transition: 'border-color 0.15s',
                      }}
                    >
                      <span style={{ fontSize: '1.4rem' }}>{opt.emoji}</span>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={saving}
                style={{ borderRadius: '0.75rem', padding: '0.75rem', fontWeight: 600 }}
              >
                {saving ? 'Setting up…' : 'Get started →'}
              </button>
            </form>
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-light)', marginTop: '1rem' }}>
          You can edit these details or add more children at any time.
        </p>
      </div>
    </div>
  );
}
